import React, { useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import { toast} from 'react-toastify';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import Participant from "./Participant";
import VideoCallControlBar from "./VideoCallControlBar";
import SelectMediaDeviceComponent from './SelectMediaDeviceComponent';

import {
  muteYourAudio,
  muteYourVideo,
  switchToCamera,
  switchToMic,
  unmuteYourAudio,
  unmuteYourVideo,
} from './helpers';
import {
  useDevices,
  VCControlAction,
  useVCControl,
} from './VCHooks';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});

function getWindowDimension() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

const Room = ({ roomName, room, leaveRoom }) => {
  const [participants, setParticipants] = useState([]);
  const [localAudioOn, setLocalAudioOn] = useState(true);
  const [localVideoOn, setLocalVideoOn] = useState(true);
  const [deviceMenuVisible, setDeviceMenuVisible] = useState(false);

  const [videoAreaHeight, setVideoAreaHeight] = useState(0);

  const controlBarRef = React.useRef();

  // Dynamically update the video area height
  const handleVideoAreaUpdate = () => {
    const windowDimension = getWindowDimension();
    const controlBarHeight = controlBarRef.current.getBoundingClientRect().height;
    setVideoAreaHeight(windowDimension.height - controlBarHeight);
  }

  const { audioInputDevices, videoInputDevices, audioOutputDevices } = useDevices();
  const { VCControlState, VCControlDispatch } = useVCControl();

  useEffect(() => {
    handleVideoAreaUpdate();

    window.addEventListener('resize', handleVideoAreaUpdate);
    return () => {
      if (deviceMenuVisible) {
        setDeviceMenuVisible(false);
      }
      window.removeEventListener('resize', handleVideoAreaUpdate)
    };
  }, []);

  useEffect(() => {
    const participantConnected = (participant) => {
      setParticipants((prevParticipants) => [...prevParticipants, participant]);
    };

    const participantDisconnected = (participant) => {
      setParticipants((prevParticipants) =>
        prevParticipants.filter((p) => p !== participant)
      );
    };

    room.on("participantConnected", participantConnected);
    room.on("participantDisconnected", participantDisconnected);
    room.participants.forEach(participantConnected);
    room.once('disconnected', (room, error) => {
      if (error) {
        toast.error(`You were disconnected from the Room: ${error.message}`);
        leaveRoom();
      }
    })


    return () => {
      room.off("participantConnected", participantConnected);
      room.off("participantDisconnected", participantDisconnected);
    };
  }, [room]);

  const updateLocalAudioAvailability = () => {
    if (localAudioOn) {
      muteYourAudio(room);
      setLocalAudioOn(false);
    } else {
      unmuteYourAudio(room);
      setLocalAudioOn(true);
    }
  }

  const updateLocalVideoAvailability = () => {
    if (localVideoOn) {
      muteYourVideo(room);
      setLocalVideoOn(false);
    } else {
      unmuteYourVideo(room);
      setLocalVideoOn(true);
    }
  }

  const openMediaDeviceSelection = () => {
    setDeviceMenuVisible(true);
  }

  const closeMediaDeviceSelection = () => {
    setDeviceMenuVisible(false);
  }

  const confirmMediaDeviceSelection = (ids) => {
    setDeviceMenuVisible(false);

    if (ids.newAudioInputId !== VCControlState.activeAudioInputId) {
      localStorage.setItem('vcActiveAudioInputId', ids.newAudioInputId);
      switchToMic(room, ids.newAudioInputId);
    }

    if (ids.newVideoInputId !== VCControlState.activeVideoInputId) {
      localStorage.setItem('vcActiveVideoInputId', ids.newVideoInputId);
      switchToCamera(room, ids.newVideoInputId);
    }

    if (ids.newAudioOutputId !== VCControlState.activeAudioOutputId) {
      localStorage.setItem('vcActiveAudioOutputId', ids.newAudioOutputId);
    }

    VCControlDispatch({
      type: VCControlAction.SetMultipleDeviceId,
      ids: {
        activeAudioInputId: ids.newAudioInputId,
        activeVideoInputId: ids.newVideoInputId,
        activeAudioOutputId: ids.newAudioOutputId,  // The output device will automatically switched when VCControlState.activeAudioOutputId is updated
      },
    })
  }

  const enableMediaSelection = audioInputDevices.length > 0 && videoInputDevices.length > 0 && audioOutputDevices.length > 0;

  return (
    <Box
      sx={{
        backgroundColor: 'black',
        position: 'fixed',
        left: 0,
        top: 0,
        minHeight: '100%',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: videoAreaHeight,
          position: 'absolute',
          flexDirection: 'row',
          justifyContent: 'center',
        }}
      >
        {
          participants.length > 0 && 
          <Box 
            sx={{
              width: '100vw',
              height: videoAreaHeight,
              display: 'flex',
              position: 'absolute',
              left: 0,
            }}
          >
            <Participant
              key={participants[0].sid}
              participant={participants[0]}
            />
          </Box>
        }
        {
          room &&
          <Box 
            sx={
              participants.length > 0 ? {
                position: 'absolute',
                width: '25vw',
                right: 0,
                top: 0,
              } :
              {
                width: '100vw',
                height: videoAreaHeight,
                display: 'flex',
                position: 'absolute',
                top: 0,
                left: 0,
              }
            }
          >
            <Participant
              isLocal
              key={room.localParticipant.sid}
              participant={room.localParticipant}
            />
          </Box>
        }
      </Box>
      <VideoCallControlBar
        ref={controlBarRef}
        roomName={roomName}
        isAudioOn={localAudioOn}
        isVideoOn={localVideoOn}
        updateAudioStatus={updateLocalAudioAvailability}
        updateVideoStatus={updateLocalVideoAvailability}
        endCall={leaveRoom}
        openMediaDeviceSelection={openMediaDeviceSelection}
        enableMediaSelection={enableMediaSelection}
      />
			<ThemeProvider theme={darkTheme}>
        <Modal
          open={deviceMenuVisible}
          onClose={closeMediaDeviceSelection}
          aria-label="Setup Video and Audio"
          sx={{ overflow: 'scroll', height: '100%'}}
        >
          <SelectMediaDeviceComponent
            audioInputDevices={audioInputDevices}
            videoInputDevices={videoInputDevices}
            audioOutputDevices={audioOutputDevices}
            activeAudioInputId={VCControlState.activeAudioInputId}
            activeVideoInputId={VCControlState.activeVideoInputId}
            activeAudioOutputId={VCControlState.activeAudioOutputId}
            onConfirm={confirmMediaDeviceSelection}
            onCancel={closeMediaDeviceSelection}
          />
        </Modal>
      </ThemeProvider>
    </Box>
  );
};

export default Room;
